import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../../components/DataTable/DataTable';
import Badge from '../../../components/ui/Badge';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { StoreCatalog } from './useData';

export const moduleConfig = {
  module: {
    title: 'Store catalogs',
    permissionModuleKey: 'store_catalogs',
    route: {
      list: '/supplies/store-catalogs',
      new: '/supplies/store-catalogs/new',
      detail: (id: number) => `/supplies/store-catalogs/${id}`,
      edit: (id: number) => `/supplies/store-catalogs/${id}/edit`},
    api: {
      list: '/store-catalogs',
      detail: (id: number) => `/store-catalogs/${id}`}},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search by catalog name…' },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '',
      fields: ['name', 'isActive']},
  ],
  filters: {
    fields: [
      {
        key: 'name',
        label: 'Name',
        placeholder: 'All names',
        getValue: (item: StoreCatalog) => item.name,
        options: []},
      {
        key: 'isActive',
        label: 'Active',
        type: 'boolean' as const,
        getValue: (item: StoreCatalog) => (item.isActive ? 'Yes' : 'No'),
        options: [
          { value: 'Yes', label: 'Yes' },
          { value: 'No', label: 'No' },
        ]},
    ],
    applyFilter: (items: StoreCatalog[], filters: Record<string, string[]>) => {
      return items.filter((item) => {
        if (filters.name?.length) {
          const v = item.name || '';
          if (!filters.name.some((f) => v.toLowerCase().includes(String(f).toLowerCase()))) return false;
        }
        if (filters.isActive?.length) {
          const s = item.isActive ? 'Yes' : 'No';
          if (!filters.isActive.includes(s)) return false;
        }
        return true;
      });
    }},
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'New catalog',
        showTitle: false,
        variant: 'primary' as const,
        href: '/supplies/store-catalogs/new'},
    ]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: StoreCatalog) => item.id,
  },
  emptyStates: {
    noData: 'Add a catalog to group store items (e.g. IMPA, ship chandler).',
    noSearchResults: 'No catalogs match your search.',
    noFilterResults: 'No catalogs match the filters.',
  },
  delete: {
    title: 'Delete catalog',
    message: 'Delete this catalog? It must not be used by store items or item groups.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete catalog.',
    handler: async (id: number) => {
      const { deleteStoreCatalog } = await import('../../../services/mutations');
      await deleteStoreCatalog(id);
    }},
  utils: {
    formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '')},
  detail: {
    subtitle: 'Details',
    notFoundTitle: 'Not found',
    notFoundMessage: 'This catalog does not exist.',
    notFoundBackText: 'Back to list',
    defaultTitle: 'Catalog',
    getTitle: (item: StoreCatalog) => item.name || `Catalog #${item.id}`},
  form: {
    new: { title: 'New catalog', subtitle: 'Display name for this product catalog', submitText: 'Create' },
    edit: { title: 'Edit catalog', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    create: async (data: Partial<StoreCatalog>) => {
      const { createStoreCatalog } = await import('../../../services/mutations');
      return createStoreCatalog(data as Record<string, unknown>);
    },
    update: async (id: number, data: Partial<StoreCatalog>) => {
      const { updateStoreCatalog } = await import('../../../services/mutations');
      return updateStoreCatalog(id, data as Record<string, unknown>);
    }},
  fields: [
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'e.g. IMPA 2023',
      validation: { required: true, minLength: 1, maxLength: 200 }},
    {
      name: 'sortOrder',
      label: 'Sort order',
      type: 'number',
      defaultValue: 0},
    {
      name: 'isActive',
      label: 'Active',
      type: 'switch',
      switchLabel: 'Active',
      defaultValue: true},
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'main',
        title: 'Catalog',
        icon: '',
        gridCols: 'md:grid-cols-12',
        fields: ['name', 'sortOrder', 'isActive'],
        fieldSpans: {
          name: 'md:col-span-8',
          sortOrder: 'md:col-span-4',
          isActive: 'md:col-span-6'},
        collapsible: false},
    ]} as FormLayout};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<StoreCatalog>[] => [
  { key: 'name', header: 'Name', sortable: true, render: (v) => <TextCell value={v} /> },
  {
    key: 'isActive',
    header: 'Active',
    sortable: true,
    render: (v, row) => (
      <Badge variant={v ?? row?.isActive ? 'success' : 'danger'} className="shrink-0 border rounded-md px-2 py-0.5">
        {v ?? row?.isActive ? 'Yes' : 'No'}
      </Badge>
    )},
  { key: 'sortOrder', header: 'Order', sortable: true, render: (v) => <TextCell value={v != null ? String(v) : undefined} /> },
  { key: 'createdAt', header: 'Created', sortable: true, render: (v) => formatDate(v) },
];

export const getDetailSections = (item: StoreCatalog, formatDate: (s?: string) => string) => [
  {
    title: 'Overview',
    fields: [
      { label: 'Name', value: item.name },
      { label: 'Sort order', value: item.sortOrder != null ? String(item.sortOrder) : '—' },
      { label: 'Active', value: item.isActive ? 'Yes' : 'No' },
      { label: 'Created', value: formatDate(item.createdAt) },
      { label: 'Updated', value: formatDate(item.updatedAt) },
    ]},
];

export const getStatusBadges = () => null;

export const getInitialFormData = (): Partial<StoreCatalog> => ({
  name: '',
  sortOrder: 0,
  isActive: true});

export const mapItemToFormData = (item: StoreCatalog): Partial<StoreCatalog> => ({
  name: item.name,
  sortOrder: item.sortOrder ?? 0,
  isActive: item.isActive ?? true});

export type ModuleConfig = typeof moduleConfig;

const TableActionsCell: React.FC<{
  item: StoreCatalog;
  onDelete: (id: number) => void;
  canDelete?: boolean;
  canEdit?: boolean;
}> = ({ item, onDelete, canDelete = true, canEdit = true }) => (
  <div className="flex items-center flex-nowrap">
    <Link
      to={moduleConfig.module.route.detail(item.id)}
      className="p-1 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
      onClick={(e) => e.stopPropagation()}
      title="View"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    </Link>
    {canEdit && (
      <Link
        to={moduleConfig.module.route.edit(item.id)}
        className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
        onClick={(e) => e.stopPropagation()}
        title="Edit"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      </Link>
    )}
    {canDelete && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(item.id);
        }}
        className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
        title="Delete"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
    )}
  </div>
);

export const getTableActions = (
  item: StoreCatalog,
  onDelete: (id: number) => void,
  _c?: unknown,
  _ctx?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean },
): React.ReactNode => (
  <TableActionsCell item={item} onDelete={onDelete} canDelete={options?.canDelete !== false} canEdit={options?.canEdit !== false} />
);

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  return null;
};
